import React, { useEffect, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import UserLibraryFilter from './UserLibraryFilter';
import { CareUserContext } from './provider/careUserContext';
import TabDetail from './TabDetail';
import ColumnTableModal from './ColumnTableModal';
import ImportModal from '../BomLibrary/ImportModal';
import ConfirmationModalForUpload from '../BomLibrary/ConfirmationModalForUpload';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { treadFile } from '../../../Components/helper';
import { getPreReadSignedWriteUrl } from '../../../services/s3Services';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import { importCareUserLib, deleteUserInstructionDetailAction, getAllUserTabs } from './store/UserInstruction.action';
import ErrorModal from '../BomLibrary/ErrorModal';

function TabDetailsLandingPage({ handleCheckAll }) {
  const { _id } = useParams();
  const dispatch = useDispatch();
  const param = useParams();
  const docRef = useRef(null);
  const { t } = useTranslation('common');

  const { state, setState, fetchTabData } = useContext(CareUserContext);

  const getNameById = async () => {
    const item = state?.UserInstructionList?.find((element) => element?._id === _id);
    if (item) {
      setState((prev) => ({
        ...prev,
        tabName: item?.name,
      }));
    }
  };

  useEffect(() => {
    if (_id) {
      getNameById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, state?.UserInstructionList]);

  const closeUploadModal = () => {
    setState((prevState) => ({
      ...prevState,
      showUploadModal: false,
    }));
  };

  const togglePreview = () => {
    setState((prevState) => ({
      ...prevState,
      confirmUploadProcess: false,
    }));
  };

  const hasDuplicateTestingCode = (data) => {
    if (state?.tabData?.length > 0) {
      const testData = [];
      const codeColumn = state.columns?.find((e) => e.name === 'Code');
      const result = state?.tabData?.flatMap(
        (innerArray) =>
          innerArray
            .filter((item) => item.columnId === codeColumn?._id) // Find objects where columnId is 3
            .map((item) => item.value), // Map to only 'code' values
      );
      const array = result?.find((element) => data.some((obj) => obj['Code*'] === element || obj['Code *'] === element));
      if (array) {
        testData.push(array);
      }
      return testData;
    }
    return [];
  };
  const handleFile = async () => {
    if (docRef?.current) {
      const uploadedDoc = await docRef.current.selectedDocument();
      if (uploadedDoc) {
        const binary = uploadedDoc.document;
        binary.fileName = binary.name;
        setState((prev) => ({ ...prev, filesSelected: binary }));
        if (binary.size > 5000000) {
          toasterMsg(t('CARE_LABEL.FILE_ERROR'), 'danger');
        } else {
          const convertedXlsxToArray = await treadFile(binary, true);
          const params = {
            xls: binary,
            checked: state?.checked,
            tabId: _id,
          };

          const checkDuplicateTestingCode = hasDuplicateTestingCode(convertedXlsxToArray.data);
          if (checkDuplicateTestingCode?.length > 0) {
            setState((prev) => ({ ...prev, confirmUploadProcess: true }));
          } else {
            const res = await dispatch(importCareUserLib(params, t));
            if (res?.code === 200) {
              setState((prev) => ({ ...prev, checked: false, selectedIdArray: [], tabData: [] }));
              await fetchTabData();
              if (res?.data?.logErr?.length > 0) {
                setState((prev) => ({
                  ...prev,
                  showErrModal: true,
                  totalRecordsToUpload: res?.data?.totalCount,
                  errorRecordCount: res?.data?.errorDataCount,
                  successRecordCount: res?.data?.successDataCount,
                  errFile: res?.data?.file,
                }));
              }
            }
          }
          closeUploadModal();
        }
      } else {
        toasterMsg(t('CARE_LABELS.LIBRARY_FILTER.FILE_EXTENSION'), 'danger');
      }
    }
  };

  const handleNoButtonClick = async () => {
    setState((prev) => ({ ...prev, confirmUploadProcess: false, checked: false }));
  };

  const handleYesButtonClick = async () => {
    setState((prev) => ({ ...prev, confirmUploadProcess: false, checked: true }));
    const params = {
      xls: state?.filesSelected,
      checked: state?.checked,
      tabId: _id,
    };
    const res = await dispatch(importCareUserLib(params, t));
    if (res.code === 200) {
      setState((prev) => ({ ...prev, checked: false, selectedIdArray: [], tabData: [] }));
      await fetchTabData();
      if (res?.data?.logErr?.length > 0) {
        setState((prev) => ({
          ...prev,
          showErrModal: true,
          totalRecordsToUpload: res?.data?.totalCount,
          errorRecordCount: res?.data?.errorDataCount,
          successRecordCount: res?.data?.successDataCount,
          errFile: res?.data?.file,
        }));
      }
    }
  };

  const toggleErr = () => {
    setState((prev) => ({ ...prev, showErrModal: false }));
  };

  const handleErrorDownload = async () => {
    const errFile = state?.errFile;
    if (!errFile) return;
    let href;
    if (errFile.startsWith('http')) {
      href = errFile;
    } else if (errFile.startsWith('/')) {
      href = `${window.location.origin}${errFile}`;
    } else {
      const res = await getPreReadSignedWriteUrl({ objectKey: errFile });
      if (res?.code !== 200 || !res?.data) return;
      href = res.data;
    }
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', errFile.split('/').pop() || errFile);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toggleErr();
  };

  const deleteById = async () => {
    const params = {
      id: param?._id,
      rowId: state?.deleteId ? [state?.deleteId] : state?.selectedIdArray,
    };
    let res = false;
    if (state?.deleteId || state?.selectedIdArray?.length > 0) {
      res = await dispatch(deleteUserInstructionDetailAction(params, t));
    }
    if (res?.blocked) {
      return;
    }
    if (res?.success) {
      await dispatch(getAllUserTabs(param?._id));
    }
    setState((prev) => ({ ...prev, deleteId: null, showConfirmationPopup: false }));
  };

  useEffect(() => {
    return () => {
      setState((prev) => ({ ...prev, selectedIdArray: [], tabName: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container">
          <h4 className="title">{state?.tabName} </h4>
        </div>
        <UserLibraryFilter selectedLibrary={state?.selectedIdArray} handleCheckAll={handleCheckAll} showButtons />
      </div>
      <div className="common-user-table">
        <TabDetail />
      </div>
      {state?.showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={state?.showConfirmationPopup}
          onConfirm={deleteById}
          loading={false}
          title={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.DELETE_ROW')}
          message={`${t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.CONFIRM_DELETE_MESSAGE')} ${state?.tabName}?`}
          toggle={() => setState((prev) => ({ ...prev, showConfirmationPopup: false }))}
        />
      )}

      {state?.showColumnConfig && <ColumnTableModal tabName={state?.tabName} />}
      {state?.showUploadModal && (
        <ImportModal showUploadModal={state?.showUploadModal} closeUploadModal={closeUploadModal} docRef={docRef} addDocument={handleFile} loader={false} />
      )}
      {state?.confirmUploadProcess && (
        <ConfirmationModalForUpload
          onOpen={state?.confirmUploadProcess}
          toggle={togglePreview}
          title={t('CARE_LABEL.UPLOAD_EXCEL')}
          description={t('CARE_LABEL.UPLOAD_DISCRIPTION')}
          checked={state?.checked}
          handleNoButtonClick={handleNoButtonClick}
          handleYesButtonClick={handleYesButtonClick}
        />
      )}
      {state?.showErrModal && (
        <ErrorModal
          totalCount={state?.totalCount}
          successCount={state?.successCount}
          errorCount={state?.errorCount}
          title={t('CARE_LABEL.UPLOAD_EXCEL')}
          onOpen={state?.showErrModal}
          toggle={toggleErr}
          handleErrorDownload={handleErrorDownload}
        />
      )}
    </>
  );
}

export default TabDetailsLandingPage;
